import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DEFAULT_ERROR_TEXT } from '@utils';
import ProductionPolicyDetailsWidget from './tabs/summary-tab/PolicyDetailsWidget';
import TabsLayout from '@components/layout/tabs/TabsLayout';
import { useAppSelector } from '@redux/hooks';
import { productionPolicyPage } from './content';
import { cloneDeep } from 'lodash';
import {
  getPlanPolicySpecificMedicalCovers,
  getPolicyDetailsQuery,
  getPolicyPersonDetailsQuery,
} from './queries';
import { useLazyQuery } from '@apollo/client';
import { GetProductionPolicyData, convertToPolicyPersons } from './functions';
import { IPolicyPersonDetails, IProductionDetailsSummary } from '.';
import { MedicalInsuredTableWidget } from '../../proposal/page/medical/custom-widgets/insured-table-widget/MedicalInsuredTableWidget';
import { LookupToList, getFirePlanCoversLov, getPlanCoversLov } from './utils';
import PolicyMedicalGroupDetailsWidget from './tabs/summary-tab/PolicyMedicalGroupDetailsWidget';
import { TravelInsuredTableWidget } from '../../proposal/page/travel/custom-widgets/insured-table-widget/TravelInsuredTableWidget';
import ProductionAmendmentListing from './tabs/amendments-tab/ProductionAmendmentListing';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import LeftPanel from './sections/LeftPanel';
import PropertyDescriptionWidget from './tabs/summary-tab/property-description/PropertyDescriptionWidget';
import CostAndChargesWidget from './tabs/summary-tab/CostAndChargesWidget';
import TravelDetailsWidget from './tabs/summary-tab/TravelDetailsWidget';
import MotorDetailsWidget from './tabs/summary-tab/motor-details/MotorDetailsWidget';
import MedicalDetailsWidget from './tabs/summary-tab/MedicalDetailsWidget';
import MarineHullDetailsWidget from './tabs/summary-tab/marine-hull-details/MarineHullDetailsWidget';
import MarineDetailsWidget from './tabs/summary-tab/marine-details/MarineDetailsWidget';
import ExpatDetailsWidget from './tabs/summary-tab/expat-details/ExpatDetailsWidget';
import PolicyCoversWidget from './tabs/summary-tab/PolicyCoversWidget';
import DescriptionOfRiskWidget from './tabs/summary-tab/description-of-risk/DescriptionOfRiskWidget';
import OfferDetailsWidget from './tabs/summary-tab/offer-details/OfferDetailsWidget';
import PolicyPersonsTab from './tabs/policy-persons-tab/PolicyPersonsTab';
import PaymentTermsSchedule from './tabs/payment-terms-tab/payment-terms-schedule/PaymentTermsSchedule';
import { CommissionSplitWidget } from './tabs/summary-tab/commission-split/CommissionSplitWidget';
import BankersBlanketWidget from './tabs/summary-tab/bankers-blanket-details/BankersBlanketWidget';
import DocumentsTab from './tabs/documents-tab/DocumentsTab';
import {
  GenericLines,
  getGenericLine,
  getLineCategory,
  hasProperty,
  hasRisk,
  LineCategory,
} from '@utils/helper-utils';
import Stepper from './sections/Stepper';
import { useTabsLayoutStyles } from '@styles/sharedStyles';

const ProductionPolicyPage: React.FC = () => {
  const { classes } = useTabsLayoutStyles();

  const user = useAppSelector((state) => state.user);
  const { id: entityId } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<IProductionDetailsSummary>();
  const [personData, setPersonData] = useState<IPolicyPersonDetails>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    policyCovers: {},
  });

  const lineCategory = getLineCategory(data?.LineId?.ExternalCode);
  const genericLine = getGenericLine(data?.LineId?.ExternalCode);

  const [getPolicyDetailsLazy] = useLazyQuery(getPolicyDetailsQuery);
  const [getPolicyPersonDetailsLazy] = useLazyQuery(
    getPolicyPersonDetailsQuery
  );
  const [getNonSelectedPlanCoverLazy] = useLazyQuery(
    getPlanPolicySpecificMedicalCovers()
  );

  const getPlanCoverDetails = async (
    planId: string,
    planCoverIds: string[]
  ) => {
    const { data } = await getNonSelectedPlanCoverLazy({
      variables: {
        selectedPlanID: planId,
        selectedPlanCoverIDs: planCoverIds,
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return data;
  };

  const initialize = async () => {
    setLoading(true);
    try {
      const { data: policyDetails } = await getPolicyDetailsLazy({
        variables: { id: entityId, state: 'LATEST' },
        errorPolicy: 'all',
      });

      const summaryData = GetProductionPolicyData(policyDetails);

      if (summaryData.LineId.ExternalCode?.toString() === '6') {
        const planCoverDetails = await getPlanCoverDetails(
          summaryData.PlanID.Id,
          []
        );
        const lovCovers = getPlanCoversLov(planCoverDetails);
        setLovs((prevLovs) => ({
          ...prevLovs,
          policyCovers: lovCovers,
        }));
      }

      const lists = LookupToList(policyDetails);
      setLovs((prevLovs) => ({
        ...prevLovs,
        medicalClasses: lists.medicalClasses,
        matters: lists.matters,
        geoLocations: lists.geoLocations,
        phoneType: lists.phoneType,
        occupationClasses: lists.occupationClasses,
        nationalities: lists.nationalities,
        countries: lists.countries,
        insuredRelations: lists.insuredRelations,
        genders: lists.genders,
        planCovers: getFirePlanCoversLov(policyDetails),
      }));

      setData(summaryData);
      setLoading(false);
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const loadPolicyPersons = async () => {
    const { data } = await getPolicyPersonDetailsLazy({
      variables: { id: entityId, state: 'LATEST' },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });
    const summaryData = convertToPolicyPersons(data);
    setPersonData({
      Persons: summaryData,
    });
  };

  useEffect(() => {
    initialize();
    loadPolicyPersons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTabs = useMemo(() => {
    const tabs = cloneDeep(productionPolicyPage);

    if (!data) {
      return tabs;
    }

    tabs.tabs[0].widgets[0].children = (
      <section className={classes.tabSplitBody}>
        <div className={classes.tabMainBody}>
          <ProductionPolicyDetailsWidget data={data} />
          {lineCategory === LineCategory.Motor && (
            <MotorDetailsWidget
              generalData={{
                policy: {
                  id: entityId,
                },
                line: {
                  externalCode: data.LineId.ExternalCode,
                },
              }}
              allowPolicyCorrection={
                user.info.businessUser_AllowPolicyCorrection
              }
              onDataRefresh={initialize}
            />
          )}
          {lineCategory === LineCategory.Expat && (
            <ExpatDetailsWidget
              data={data}
              lovs={lovs}
              allowPolicyCorrection={
                user.info.businessUser_AllowPolicyCorrection
              }
              onDataRefresh={initialize}
            />
          )}

          {data?.LineId?.ExternalCode === '19' && (
            <MedicalDetailsWidget data={data} />
          )}

          {lineCategory === LineCategory.Travel && (
            <TravelDetailsWidget data={data} />
          )}

          {lineCategory === LineCategory.Generic &&
            genericLine === GenericLines.MARINE && (
              <MarineDetailsWidget
                allowPolicyCorrection={
                  user.info.businessUser_AllowPolicyCorrection
                }
                data={data}
                lovs={lovs}
                onDataRefresh={initialize}
              />
            )}

          {hasRisk(data?.LineId?.ExternalCode) && (
            <DescriptionOfRiskWidget
              data={data}
              lovs={lovs}
              allowPolicyCorrection={
                user.info.businessUser_AllowPolicyCorrection
              }
              onDataRefresh={initialize}
            />
          )}
          {lineCategory === LineCategory.Generic &&
            genericLine === GenericLines.MARINE_HULL && (
              <MarineHullDetailsWidget
                data={data}
                allowPolicyCorrection={
                  user.info.businessUser_AllowPolicyCorrection
                }
                onDataRefresh={initialize}
              />
            )}

          {data?.Covers?.length > 0 && (
            <PolicyCoversWidget
              currencySymbol={data?.PolicyCurrency?.Symbol || ''}
              data={data.Covers}
              lineExternalCode={Number(data.LineId.ExternalCode)}
            />
          )}
          {data?.Bankers?.length > 0 &&
            lineCategory === LineCategory.Generic &&
            genericLine === GenericLines.BBB && (
              <BankersBlanketWidget
                data={data.Bankers}
                lovs={lovs}
                onDataRefresh={initialize}
              />
            )}
          {data?.PropertyDescription?.length > 0 &&
            hasProperty(data?.LineId?.ExternalCode) && (
              <PropertyDescriptionWidget
                currencySymbol={data?.PolicyCurrency?.Symbol}
                data={data.PropertyDescription}
                lovs={lovs}
                allowPolicyCorrection={
                  user.info.businessUser_AllowPolicyCorrection
                }
                onDataRefresh={initialize}
              />
            )}

          <CostAndChargesWidget data={data} />

          {data?.LineId?.ExternalCode === '6' && (
            <PolicyMedicalGroupDetailsWidget lovs={lovs} />
          )}

          {lineCategory === LineCategory.Medical && (
            <MedicalInsuredTableWidget data={data as any} />
          )}

          {lineCategory === LineCategory.Travel && (
            <TravelInsuredTableWidget data={data as any} />
          )}
        </div>

        <div className={classes.tabRightSection}>
          <OfferDetailsWidget data={data} />
          <CommissionSplitWidget data={data} />
        </div>
      </section>
    );

    tabs.tabs[1].widgets[0].children = (
      <PolicyPersonsTab
        data={personData}
        lovs={lovs}
        planDetails={{
          lineExternalCode: data?.LineId?.ExternalCode,
          PlanMaxNbInsured: data?.PlanID?.MaxNbInsured,
        }}
        onDataRefresh={loadPolicyPersons}
      />
    );

    tabs.tabs[2].widgets[0].children = <DocumentsTab data={data} />;

    tabs.tabs[3].widgets[0].children = (
      <PaymentTermsSchedule
        policyId={entityId}
        policyNumber={data?.PolicyNumber}
      />
    );

    tabs.tabs[4].widgets[0].children = (
      <ProductionAmendmentListing
        policyId={entityId}
        allowEndorsement={user.info.businessUser_AllowEndorsement}
        allowPolicyCorrection={user.info.businessUser_AllowPolicyCorrection}
        transferredToAccounting={data.TransferredToAccounting}
        policyDetails={{
          policyNumber: data.PolicyNumber,
          policyEffectiveDate: data.PolicyEffectiveDate,
          policyExpiryDate: data.PolicyExpiryDate,
          access: user.info?.businessUser_Access?.Title?.toUpperCase(),
        }}
      />
    );
    return tabs;
  }, [data, user, entityId, personData]);

  return (
    <TabsLayout
      name="productionPolicyPage"
      layout={renderTabs}
      loading={loading || !data}
      leftChildren={<LeftPanel data={data} />}
      mainChildren={<Stepper activeStep={data?.Status} />}
    />
  );
};

export default ProductionPolicyPage;
